// Vendors
import Vue from 'vue';
import Axios from 'axios';
import VueAxios from 'vue-axios';
import DOMPurify from 'dompurify';
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate';
import vSelect from 'vue-select';
import VueSweetalert2 from 'vue-sweetalert2';
import 'sweetalert2/dist/sweetalert2.all.min.js';
import DatePicker from 'v-calendar/lib/components/date-picker.umd';
import SwatchPicker from 'vue-color/src/components/Swatches';
import { VueMaskDirective } from 'v-mask';
import VueCookies from 'vue-cookies';
import { VueTelInput } from 'vue-tel-input';
import FloatingVue from 'floating-vue'

// Languages
import i18n from './langs/i18n.js';

// Styles
import '@/assets/scss/mini-framework.scss';
import 'vue-select/dist/vue-select.css';
import '@/assets/scss/vue-select.override.scss';
import '@/assets/scss/vue-sweetalert2.override.scss';
import '@/assets/scss/vue-color.override.scss';
import '@/assets/scss/v-calendar.override.scss';
import 'cropperjs/dist/cropper.css';
import '@/assets/scss/vue-tel-input.override.scss';
import 'floating-vue/dist/style.css';
import '@/assets/scss/floating-vue.overrides.scss';

// Core
import App from './App.vue';
import AppConfig from './.env';
import router from './router';
import store from './store/index';

// Layouts
import Default from '@/layouts/Default.vue';
import GuestLayout from '@/layouts/Guest.vue';

// Helpers
import addVeeValidateRules from '@/utils/VeeValidateRules.js';
import SanitizeUtils from '@/utils/SanitizeUtils';

// Custom components
import FButton from '@/components/core/FButton.vue';
import FBadge from '@/components/core/FBadge.vue';
import FProgressBar from '@/components/core/FProgressBar.vue';
import FAlert from '@/components/core/FAlert.vue';
import FMessageCounter from '@/components/core/FMessageCounter.vue';
import FDropdown from '@/components/core/FDropdown.vue';
import FCheckbox from '@/components/forms/FCheckbox.vue';
import FInput from '@/components/forms/FInput.vue';
import FInputWrapper from '@/components/forms/FInputWrapper.vue';
import FTextarea from '@/components/forms/FTextarea.vue';
import FRadioButton from '@/components/forms/FRadioButton.vue';
import FList from '@/components/layout/FList.vue';
import FMessageHistory from '@/components/layout/FMessageHistory.vue';
import FMessage from '@/components/layout/FMessage.vue';
import FIcon from '@/components/core/FIcon.vue';
import FTimeElapsed from '@/components/core/FTimeElapsed.vue';
import FFileInput from '@/components/core/FFileInput.vue';
import FNav from '@/components/layout/FNav.vue';
import FTabs from '@/components/layout/FTabs.vue';
import FModal from '@/components/layout/FModal.vue';
import FModalHeader from '@/components/layout/FModalHeader.vue';
import FModalBody from '@/components/layout/FModalBody.vue';
import FModalFooter from '@/components/layout/FModalFooter.vue';
import FAccordion from '@/components/layout/FAccordion.vue';
import FAccordionItem from '@/components/layout/FAccordionItem.vue';
import FTooltip from '@/components/core/FTooltip.vue';

Vue.component('f-button', FButton);
Vue.component('f-badge', FBadge);
Vue.component('f-progress-bar', FProgressBar);
Vue.component('f-alert', FAlert);
Vue.component('f-message-counter', FMessageCounter);
Vue.component('f-dropdown', FDropdown);
Vue.component('f-list', FList);
Vue.component('f-checkbox', FCheckbox);
Vue.component('f-input', FInput);
Vue.component('f-input-wrapper', FInputWrapper);
Vue.component('f-textarea', FTextarea);
Vue.component('f-form-radio', FRadioButton);
Vue.component('f-message-history', FMessageHistory);
Vue.component('f-message', FMessage);
Vue.component('f-icon', FIcon);
Vue.component('f-time-elapsed', FTimeElapsed);
Vue.component('f-file-input', FFileInput);
Vue.component('f-nav', FNav);
Vue.component('f-tabs', FTabs);
Vue.component('f-modal', FModal);
Vue.component('f-modal-header', FModalHeader);
Vue.component('f-modal-body', FModalBody);
Vue.component('f-modal-footer', FModalFooter);
Vue.component('f-accordion', FAccordion);
Vue.component('f-accordion-item', FAccordionItem);
Vue.component('f-tooltip', FTooltip);

// Global components
Vue.component('default-layout', Default);
Vue.component('guest-layout', GuestLayout);
Vue.component('validation-provider', ValidationProvider);
Vue.component('validation-observer', ValidationObserver);
Vue.component('v-select', vSelect);
Vue.component('v-date-picker', DatePicker);
Vue.component('swatch-picker', SwatchPicker);
Vue.component('vue-tel-input', VueTelInput);

// Global directives
Vue.directive('mask', VueMaskDirective);

// Configure vendors
addVeeValidateRules(extend);

// Axios
window.axios = Axios;
Vue.prototype.$http = Axios;
Vue.prototype.$http.defaults.baseURL = AppConfig.apiServerUrl;
Vue.prototype.$http.defaults.isMobileApp = AppConfig.isMobileApp;
Vue.prototype.$http.defaults.cookieBannerDebugMode = AppConfig.cookieBannerDebugMode;
Vue.prototype.$http.defaults.headers['Cache-Control'] = 'no-store,max-age=0';

if (AppConfig.isMobileApp) {
  Vue.prototype.$http.defaults.headers['FinCrm-Mode'] = 'mobile';

  if (SanitizeUtils.getToken()) {
    Vue.prototype.$http.defaults.headers.Authorization = 'Bearer ' + SanitizeUtils.getToken();
  }
}

Vue.prototype.$http.interceptors.response.use(undefined, (error) => {
  if (
    error.response.status === 401 &&
    router.currentRoute.path !== '/logowanie' &&
    router.currentRoute.path !== '/wyloguj' &&
    router.currentRoute.path !== '/wczytywanie'
  ) {
    window.location.reload();
  }

  return Promise.reject(error);
});

// Other
Vue.config.productionTip = false;
Vue.prototype.$bus = new Vue();
Vue.use(VueAxios, Axios);
Vue.use(VueSweetalert2);
Vue.use(VueCookies);
Vue.use(FloatingVue);

// DOMPurify configuration
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  // set all elements owning target to target=_blank
  if ('target' in node) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }

  // set non-HTML/MathML links to xlink:show=new
  if (!node.hasAttribute('target') && (node.hasAttribute('xlink:href') || node.hasAttribute('href'))) {
    node.setAttribute('xlink:show', 'new');
  }
});

// Register v-pure-html directive
Vue.directive('pure-html', {
  inserted: (el, binding) => {
    if (binding.oldValue === binding.value) {
      return;
    }
    el.innerHTML = DOMPurify.sanitize(binding.value);
  },
  update: (el, binding) => {
    if (binding.oldValue === binding.value) {
      return;
    }
    el.innerHTML = DOMPurify.sanitize(binding.value);
  }
});

new Vue({
  router,
  store,
  i18n,
  render: h => h(App)
}).$mount('#app');
